import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Edit, X, Search } from 'lucide-react';
import Avatar from './Avatar';
import { getAnchoredOverlayStyle } from '../../utils/viewportPosition';

const ManagerSelect = ({
  currentManager,
  currentManagerId,
  managerOptions = [],
  isEditing = false,
  isLoading = false,
  onEdit,
  onCancel,
  onChange,
  emptyLabel = "No manager",
  selectPlaceholder = "Search manager..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filteredManagers = managerOptions.filter(manager => {
    const searchLower = searchTerm.toLowerCase();
    const name = manager.name?.toLowerCase() || '';
    const email = manager.email?.toLowerCase() || '';
    return name.includes(searchLower) || email.includes(searchLower);
  });

  useEffect(() => {
    if (isEditing) {
      setSearchTerm('');
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isEditing]);

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current || !isEditing) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle(getAnchoredOverlayStyle({
      anchorRect: rect,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      preferredWidth: Math.max(250, rect.width),
    }));
  }, [isEditing]);

  useEffect(() => {
    if (isEditing) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isEditing, updateDropdownPosition]);

  const handleSelectManager = (managerId) => {
    onChange(managerId);
    setSearchTerm('');
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        {currentManager || currentManagerId ? (
          <>
            <Avatar
              avatar={currentManager?.avatar}
              name={currentManager?.name}
              email={currentManager?.email}
              size="sm"
              showInitialOnMissing={false}
            />
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {currentManager?.name || currentManager?.email || `ID: ${currentManagerId}`}
              </span>
              {currentManager?.name && currentManager?.email && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {currentManager.email}
                </span>
              )}
            </div>
          </>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">
            {emptyLabel}
          </span>
        )}
        {onEdit && (
          <button
            onClick={onEdit}
            className="p-1 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors flex-shrink-0"
            title="Edit manager"
          >
            <Edit className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full min-w-0 sm:w-[250px]">
      <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-light-text-tertiary dark:text-dark-text-tertiary" />
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder={selectPlaceholder}
        disabled={isLoading}
        className="block w-full pl-7 pr-7 py-1.5 text-xs border border-light-border-primary dark:border-dark-border-primary rounded-md bg-light-bg-primary dark:bg-dark-bg-secondary text-light-text-primary dark:text-dark-text-primary placeholder-light-text-tertiary dark:placeholder-dark-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {onCancel && (
        <button
          onClick={onCancel}
          disabled={isLoading}
          title="Cancel"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-light-text-primary dark:hover:text-dark-text-primary rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {createPortal(
        <div
          style={dropdownStyle}
          className="overflow-auto border border-light-border-primary dark:border-dark-border-primary rounded-md bg-light-bg-primary dark:bg-dark-bg-secondary shadow-lg"
        >
          {filteredManagers.length > 0 ? (
            filteredManagers.map((manager) => {
              const isSelected = currentManagerId === manager.id || currentManager?.id === manager.id;
              return (
                <button
                  key={manager.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectManager(manager.id)}
                  disabled={isLoading}
                  className={`w-full px-2 py-1.5 text-left text-xs flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-tertiary'
                  }`}
                >
                  <Avatar
                    avatar={manager?.avatar}
                    name={manager?.name}
                    email={manager?.email}
                    size="xs"
                    showInitialOnMissing={false}
                  />
                  <span className="truncate">
                    {manager.name || manager.email}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="px-2 py-1.5 text-xs text-light-text-tertiary dark:text-dark-text-tertiary italic">
              {isLoading ? 'Loading...' : 'No managers found'}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ManagerSelect;
